import { Radio as BaseRadio } from "@base-ui/react/radio";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import clsx from "clsx";
import type React from "react";
import { type CSSProperties, forwardRef, useMemo } from "react";
import type { LABEL_POSITIONS } from "../../utils/consts";
import { generateColorVariants } from "../../utils/functions";
import {
  radioColorDarkVar,
  radioColorVar,
  radioIndicatorClass,
  radioLabelClass,
  radioLabelColumnClass,
  radioLabelColumnReverseClass,
  radioLabelRowClass,
  radioLabelRowReverseClass,
  radioRootClass,
} from "./styles.css";

export interface RadioProps {
  label?: string;
  labelPosition?: keyof typeof LABEL_POSITIONS;
  customColor?: string;
  value?: any;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  id?: string;
  className?: string;
  name?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const Radio = forwardRef(function Radio(
  {
    label,
    labelPosition = "right",
    customColor,
    value,
    disabled,
    readOnly,
    required,
    id,
    className,
    name,
    checked,
    defaultChecked,
    onChange,
  }: RadioProps,
  ref: React.Ref<HTMLElement>,
) {
  const colorVariants = useMemo(
    () => (customColor ? generateColorVariants(customColor) : undefined),
    [customColor],
  );

  const colorStyles = useMemo(() => {
    if (colorVariants) {
      return assignInlineVars({
        [radioColorVar]: colorVariants.default,
        [radioColorDarkVar]: colorVariants.dark,
      }) as unknown as CSSProperties;
    }
    return undefined;
  }, [colorVariants]);

  const flexDirectionClassMap: Record<keyof typeof LABEL_POSITIONS, string> = {
    left: radioLabelRowClass,
    right: radioLabelRowReverseClass,
    top: radioLabelColumnClass,
    bottom: radioLabelColumnReverseClass,
  };
  const flexDirectionClass = flexDirectionClassMap[labelPosition];

  const isNativeVariant = !!name;

  return (
    <label
      className={clsx(radioLabelClass, flexDirectionClass, className)}
      style={colorStyles}
      data-slot="radio-root"
    >
      {label && <span data-slot="radio-label">{label}</span>}
      {isNativeVariant ? (
        <div className={radioRootClass} data-slot="radio-control">
          <input
            ref={ref as React.Ref<HTMLInputElement>}
            type="radio"
            name={name}
            value={value}
            id={id}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            checked={checked}
            defaultChecked={defaultChecked}
            onChange={onChange}
            style={{ display: "none" }}
          />
          <div className={radioIndicatorClass} data-slot="radio-indicator" />
        </div>
      ) : (
        <BaseRadio.Root
          ref={ref}
          value={value}
          id={id}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          className={radioRootClass}
          data-slot="radio-control"
        >
          <BaseRadio.Indicator
            keepMounted
            className={radioIndicatorClass}
            data-slot="radio-indicator"
          />
        </BaseRadio.Root>
      )}
    </label>
  );
});

Radio.displayName = "Radio";
